import {formatDate} from '@angular/common';
import {PdCollectionModeEnum} from '../model/dto/enum/pd-collection-mode.enum';
import {PdExportChannelEnum} from '../model/dto/enum/pd-export-channel.enum';
import {PdProcessingKindEnum} from '../model/dto/enum/pd-processing-kind.enum';
import {PdRunStatusEnum} from '../model/dto/enum/pd-run-status.enum';
import {ProcessingStatusEnum} from '../model/dto/enum/processing-status.enum';
import {
    PD_COLLECTION_MODE_LABELS,
    PD_EXPORT_CHANNEL_LABELS,
    PD_PROCESSING_KIND_LABELS,
    PD_RUN_STATUS_LABELS,
    PROCESSING_STATUS_LABELS
} from './pd-enum-labels';

// Display helpers shared by the pd components: German labels and status chips of the processing lock and the runs,
// and the two date formats of the module.
//
// STATUS CHIPS ARE PLAIN SPANS WITH PRIMEFLEX TOKENS, NOT p-tag SEVERITIES: the shell overrides `.p-tag` globally
// (shell-alpha primeng-v20-overrides.scss, background var(--primary-800)), so every severity renders in the same
// colour (known trap, fe_core_wizard WZ-005).

/** Colour tone of a status chip. */
export type PdStatusTone = 'success' | 'info' | 'warn' | 'danger' | 'neutral';

const PD_STATUS_CHIP_BASE = 'inline-block border-round px-2 py-1 text-xs font-bold';

const PD_STATUS_TONE_CLASSES: Record<PdStatusTone, string> = {
    success: 'bg-green-50 text-green-700',
    info: 'bg-blue-50 text-blue-700',
    warn: 'bg-yellow-50 text-yellow-700',
    danger: 'bg-red-50 text-red-700',
    neutral: 'bg-gray-100 text-gray-700',
};

const UNKNOWN_LABEL = 'Unbekannt';

/** CSS classes of a status chip in the given tone. */
export function pdStatusChipClass(tone: PdStatusTone): string {
    return `${PD_STATUS_CHIP_BASE} ${PD_STATUS_TONE_CLASSES[tone]}`;
}

// ── processing lock ──────────────────────────────────────────────────────────────────────────────────────────────────

/** Locked means exactly PROCESSING (DC-12); QUEUED and FAILED are not locked. */
export function pdIsLocked(status: ProcessingStatusEnum | null | undefined): boolean {
    return status === ProcessingStatusEnum.PROCESSING;
}

export function pdProcessingStatusLabel(status: ProcessingStatusEnum | null | undefined): string {
    return status != null ? (PROCESSING_STATUS_LABELS[status] ?? UNKNOWN_LABEL) : UNKNOWN_LABEL;
}

export function pdProcessingStatusTone(status: ProcessingStatusEnum | null | undefined): PdStatusTone {
    switch (status) {
        case ProcessingStatusEnum.IDLE:
            return 'success';
        case ProcessingStatusEnum.QUEUED:
            return 'info';
        case ProcessingStatusEnum.PROCESSING:
            return 'warn';
        case ProcessingStatusEnum.FAILED:
            return 'danger';
        default:
            return 'neutral';
    }
}

/** Which job holds the lock („Sammellauf“, „Export“); '' when unknown. */
export function pdProcessingKindLabel(kind: PdProcessingKindEnum | null | undefined): string {
    return kind != null ? (PD_PROCESSING_KIND_LABELS[kind] ?? '') : '';
}

// ── runs ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export function pdRunStatusLabel(status: PdRunStatusEnum | null | undefined): string {
    return status != null ? (PD_RUN_STATUS_LABELS[status] ?? UNKNOWN_LABEL) : UNKNOWN_LABEL;
}

export function pdRunStatusTone(status: PdRunStatusEnum | null | undefined): PdStatusTone {
    switch (status) {
        case PdRunStatusEnum.RUNNING:
            return 'info';
        case PdRunStatusEnum.COMPLETED:
            return 'success';
        case PdRunStatusEnum.FAILED:
            return 'danger';
        default:
            return 'neutral';
    }
}

export function pdCollectionModeLabel(mode: PdCollectionModeEnum | null | undefined): string {
    return mode != null ? (PD_COLLECTION_MODE_LABELS[mode] ?? '') : '';
}

export function pdExportChannelLabel(channel: PdExportChannelEnum | null | undefined): string {
    return channel != null ? (PD_EXPORT_CHANNEL_LABELS[channel] ?? '') : '';
}

// ── dates ────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** An Instant as DD.MM.YYYY HH:mm in local time; '' when empty. */
export function pdFormatDateTime(value: unknown, locale: string): string {
    return value == null || value === '' ? '' : formatWith(value, 'dd.MM.yyyy HH:mm', locale);
}

/** A LocalDate as DD.MM.YYYY; an ISO date string is split, never shifted through a time zone. '' when empty. */
export function pdFormatLocalDate(value: unknown, locale: string): string {
    if (value == null || value === '') {
        return '';
    }
    if (typeof value === 'string') {
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
        if (match) {
            return `${match[3]}.${match[2]}.${match[1]}`;
        }
    }
    return formatWith(value, 'dd.MM.yyyy', locale);
}

function formatWith(value: unknown, pattern: string, locale: string): string {
    const date = value instanceof Date ? value : new Date(value as string | number);
    return Number.isNaN(date.getTime()) ? String(value) : formatDate(date, pattern, locale);
}
